var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { isNetworkError } from "../../validators";
import { clampNumber, getActionDescriptor, hash, millisecondsToSecond, toDateHourMinute, } from "../../utils";
import { logger } from "../logger";
import { EVENTS_API_REQUEST_HEADER } from "../../constants";
import fetch from "../fetch";
var noop = function () { };
var DEFAULT_RETRY = 3;
var MAX_RETRY = 5;
var MIN_RETRY = 1;
var DEFAULT_FLUSH_AT = 20;
var MAX_FLUSH_AT = 20;
var MIN_FLUSH_AT = 1;
var DEFAULT_QUEUE_SIZE = 1024 * 500; // 500kB
var MAX_QUEUE_SIZE = 1024 * 500; // 500kB
var MIN_QUEUE_SIZE = 200; // 200 bytes
var DEFAULT_FLUSH_INTERVAL = 1000 * 30; // 1 MINUTE
var MAX_FLUSH_INTERVAL = 1000 * 300; // 5 MINUTES
var MIN_FLUSH_INTERVAL = 1000 * 10; // 10 SECONDS
var EventQueue = /** @class */ (function () {
    function EventQueue(writeKey, options) {
        var _this = this;
        this.queue = [];
        this.payloadHashes = new Set();
        this.onPageLeave = function (callback) {
            // To ensure the callback is only called once even if more than one events
            // are fired at once.
            var pageLeft = false;
            var isAccessible = false;
            function handleOnLeave() {
                if (pageLeft) {
                    return;
                }
                pageLeft = true;
                callback(isAccessible);
                // Reset pageLeft on the next tick
                // to ensure callback executes for other listeners
                // when closing an inactive browser tab.
                setTimeout(function () {
                    pageLeft = false;
                }, 0);
            }
            // Catches the unloading of the page (e.g., closing the tab or navigating away).
            // Includes user actions like clicking a link, entering a new URL,
            // refreshing the page, or closing the browser tab
            // Note that 'pagehide' is not supported in IE.
            // So, this is a fallback.
            globalThis.addEventListener("beforeunload", function () {
                isAccessible = false;
                handleOnLeave();
            });
            globalThis.addEventListener("blur", function () {
                isAccessible = true;
                handleOnLeave();
            });
            globalThis.addEventListener("focus", function () {
                pageLeft = false;
            });
            // Catches the page being hidden, including scenarios like closing the tab.
            document.addEventListener("pagehide", function () {
                isAccessible = document.visibilityState === "hidden";
                handleOnLeave();
            });
            // Catches visibility changes, such as switching tabs or minimizing the browser.
            document.addEventListener("visibilitychange", function () {
                isAccessible = true;
                if (document.visibilityState === "hidden") {
                    handleOnLeave();
                }
                else {
                    pageLeft = false;
                }
            });
        };
        options = options || {};
        this.queue = [];
        this.writeKey = writeKey;
        this.url = options.url;
        this.retryCount = clampNumber(options.retryCount || DEFAULT_RETRY, MAX_RETRY, MIN_RETRY);
        this.flushAt = clampNumber(options.flushAt || DEFAULT_FLUSH_AT, MAX_FLUSH_AT, MIN_FLUSH_AT);
        this.maxQueueSize = clampNumber(options.maxQueueSize || DEFAULT_QUEUE_SIZE, MAX_QUEUE_SIZE, MIN_QUEUE_SIZE);
        this.flushIntervalMs = clampNumber(options.flushInterval || DEFAULT_FLUSH_INTERVAL, MAX_FLUSH_INTERVAL, MIN_FLUSH_INTERVAL);
        this.flushed = true;
        this.errorHandler = options.errorHandler;
        this.pendingFlush = null;
        this.timer = null;
        this.onPageLeave(function (isAccessible) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(isAccessible === false)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.flush()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); });
    }
    //#region Public functions
    EventQueue.prototype.generateMessageId = function (event) {
        return __awaiter(this, void 0, void 0, function () {
            var formattedTimestamp, eventForHashing, eventString;
            return __generator(this, function (_a) {
                formattedTimestamp = toDateHourMinute(new Date(event.original_timestamp));
                eventForHashing = __assign(__assign({}, event), { original_timestamp: formattedTimestamp });
                eventString = JSON.stringify(eventForHashing);
                return [2 /*return*/, hash(eventString)];
            });
        });
    };
    EventQueue.prototype.enqueue = function (event, callback) {
        return __awaiter(this, void 0, void 0, function () {
            var message_id, hasReachedFlushAt, hasReachedQueueSize;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        callback = callback || noop;
                        return [4 /*yield*/, this.generateMessageId(event)];
                    case 1:
                        message_id = _a.sent();
                        return [4 /*yield*/, this.isDuplicate(message_id)];
                    case 2:
                        // check if the message already exists
                        if (_a.sent()) {
                            logger.warn("Event already enqueued, try again after ".concat(millisecondsToSecond(this.flushIntervalMs), " seconds."));
                            return [2 /*return*/];
                        }
                        this.queue.push({
                            message: __assign(__assign({}, event), { message_id: message_id }),
                            callback: callback,
                        });
                        logger.log("Event enqueued: ".concat(getActionDescriptor(event.type, event.properties)));
                        if (!this.flushed) {
                            this.flushed = true;
                            this.flush();
                            return [2 /*return*/];
                        }
                        hasReachedFlushAt = this.queue.length >= this.flushAt;
                        hasReachedQueueSize = this.queue.reduce(function (acc, item) { return acc + JSON.stringify(item).length; }, 0) >=
                            this.maxQueueSize;
                        if (hasReachedFlushAt || hasReachedQueueSize) {
                            this.flush();
                            return [2 /*return*/];
                        }
                        if (this.flushIntervalMs && !this.timer) {
                            this.timer = setTimeout(this.flush.bind(this), this.flushIntervalMs);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    EventQueue.prototype.flush = function (callback) {
        return __awaiter(this, void 0, void 0, function () {
            var err_1, items, sentAt, data, done;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        callback = callback || noop;
                        if (this.timer) {
                            clearTimeout(this.timer);
                            this.timer = null;
                        }
                        if (!this.queue.length) {
                            callback();
                            return [2 /*return*/, Promise.resolve()];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        if (!this.pendingFlush) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.pendingFlush];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3: return [3 /*break*/, 5];
                    case 4:
                        err_1 = _a.sent();
                        this.pendingFlush = null;
                        throw err_1;
                    case 5:
                        items = this.queue.splice(0, this.flushAt);
                        this.payloadHashes.clear();
                        sentAt = new Date().toISOString();
                        data = items.map(function (item) { return (__assign(__assign({}, item.message), { sent_at: sentAt })); });
                        done = function (err) {
                            items.forEach(function (_a) {
                                var message = _a.message, callback = _a.callback;
                                return callback(err, message, data);
                            });
                            callback(err, data);
                        };
                        return [2 /*return*/, (this.pendingFlush = fetch("".concat(this.url), {
                                headers: EVENTS_API_REQUEST_HEADER(this.writeKey),
                                method: "POST",
                                body: JSON.stringify(data),
                                keepalive: true,
                                retries: this.retryCount,
                                retryDelay: function (attempt) { return Math.pow(2, attempt) * 1000; }, // exponential backoff
                                retryOn: function (_, error) { return _this.isErrorRetryable(error); },
                            })
                                .then(function () {
                                done();
                                return Promise.resolve(data);
                            })
                                .catch(function (err) {
                                if (typeof _this.errorHandler === "function") {
                                    done(err);
                                    return _this.errorHandler(err);
                                }
                                if (err.response) {
                                    var error = new Error(err.response.statusText);
                                    done(error);
                                    throw error;
                                }
                                done(err);
                                throw err;
                            }))];
                }
            });
        });
    };
    //#region Utility functions
    EventQueue.prototype.isErrorRetryable = function (error) {
        var _a, _b, _c;
        // Retry Network Errors.
        if (isNetworkError(error))
            return true;
        // Cannot determine if the request can be retried
        if (!(error === null || error === void 0 ? void 0 : error.response))
            return false;
        // Retry Server Errors (5xx).
        if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) >= 500 && ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) <= 599)
            return true;
        // Retry if rate limited.
        if (((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.status) === 429)
            return true;
        return false;
    };
    EventQueue.prototype.isDuplicate = function (eventId) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                // check if exists a message with identical payload within 1 minute
                if (this.payloadHashes.has(eventId))
                    return [2 /*return*/, true];
                this.payloadHashes.add(eventId);
                return [2 /*return*/, false];
            });
        });
    };
    return EventQueue;
}());
export { EventQueue };
//# sourceMappingURL=EventQueue.js.map